import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Button } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { i18n } from '@lingui/core';
import _ from 'lodash';
import { buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import AdminPage from '../AdminPage';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpTextField from '../../form/NgpTextField';
import {
    COLLECTORS_ADMIN_DESCRIPTION_FIELD,
    COLLECTORS_ADMIN_NAME_FIELD,
    COLLECTORS_ADMIN_STATUS_FIELD,
    COLLECTORS_FUNCTION_NAME,
    EDIT_SCOPE_SUBNET_QUERY_PARAM,
    IP_SCOPES_FIELD,
    TAB_COLLECTORS,
} from './CollectorsAdminConstants';
import { useFindById } from '../../../services/FormQueryService';
import { useErrorWrapper, useGenericError } from '../../../services/FormErrorService';
import useAlerts, { WARNING, createAlertConfig } from '../../../stores/AlertsStore';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { Actions } from '../../page-framework';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { CollectorDetailvCenters } from './CollectorDetailvCenters';
import CollectorDetailReadOnly from './CollectorDetailReadOnly';
import CollectorDetailIpScope from './CollectorDetailIpScope';
import NgpTextarea from '../../form/NgpTextarea';
import { buildLinkUrl } from '../../links/Link';
import { useDataService } from '../../../services/ApiDataService';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import IPAddressUtilities from '../../../utilities/IPAddressUtilities';
import { getUrlParamFromState } from '../../../utilities/PageStateUrlUtilities';
import { usePageStateValueWithDefault } from '../../../stores/PageStateStore';

const defaultFormValues = {};
const apiUrl = '/ngp/v2/inventory/collectors';

const saveButtonText = i18n._(t`Save`);
const cancelButtonText = i18n._(t`Go Back`);
const saveTitle = i18n._(t`Device Deletion Warning`);
const saveText = i18n._(
    t`One or more IP scopes in this collector have been reduced in size or removed. Any devices which are no longer monitored from within any scope will be deleted.`
);

export default function CollectorsAdminDetails() {
    const [activeTab] = usePageStateValueWithDefault('activeTab', TAB_COLLECTORS);
    const goToUrl = `/admin/${COLLECTORS_FUNCTION_NAME}?${getUrlParamFromState({ activeTab })}`;
    const [, setShowDeleteButton] = useState(false);
    const recordId = useEntityIdParam();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const handleGenericError = useGenericError();
    const formSaveService = useFormSaveService();
    const formDeleteService = useFormDeleteService();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const methods = useForm();
    const { handleSubmit, reset, setError, getValues, watch } = methods;
    const dataService = useDataService();
    const [, alertsActions] = useAlerts();
    const [ipScopesSelectionIsDirty, setIpScopesSelectionIsDirty] = useState(false);
    const [deletable, setDeletable] = useState(false);
    const [transferrableCollectors, setTransferrableCollectors] = useState([]);
    const movedSubnetsRef = useRef(new Set());
    const currentIpScopes = watch(IP_SCOPES_FIELD);
    const [showDeviceDeletionConfirmationDialog, setShowDeviceDeletionConfirmationDialog] = useState(false);

    const location = useLocation();
    const editScopeSubnetFromUrl = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(EDIT_SCOPE_SUBNET_QUERY_PARAM);
    }, [location.search]);

    const collectorLinkConfig = {
        target: recordId,
    };

    const findByIdErrorHandler = useCallback(
        (errorObject) => {
            if (errorObject.getStatus && errorObject.getStatus() === 403) {
                return createAlertConfig(WARNING, t`You do not have permissions to view collector details.`);
            }

            handleGenericError(errorObject);
        },
        [handleGenericError]
    );

    const { initialized, finding, formData, resetForm } = useFindById({
        url: `${apiUrl}/`,
        recordId,
        postFindByIdHandler: (collector) => {
            setShowDeleteButton(true);

            return collector;
        },
        resetFunc: reset,
        defaultValues: {
            ...defaultFormValues,
        },
        errorHandler: errorWrapper(findByIdErrorHandler, goToUrl),
    });

    const functionBreadCrumbs = [{ link: goToUrl, text: t`Collectors` }];
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, COLLECTORS_ADMIN_NAME_FIELD, '', {
        collectorLinkConfig,
    });

    const handleFormSubmit = useCallback(
        (submitData) => {
            const dataToSave = {
                ...formData,
                ...submitData,
            };

            if (dataToSave?.userId === 'null_user') {
                dataToSave.userId = null;
            }

            return formSaveService.save({
                url: apiUrl,
                recordId: formData._id,
                dataToSave,
                setError,
            });
        },
        [formData, formSaveService, setError]
    );
    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);
    // Save without navigating away, used for the "save & continue" flow before transferring an IP scope.
    const handleSubmitWithoutNavigate = submitWrapper(handleFormSubmit, '');

    const dirtyCheck = useCallback(
        (isDirty) => {
            return isDirty || ipScopesSelectionIsDirty;
        },
        [ipScopesSelectionIsDirty]
    );

    /**
     * Returns true when the form (including the IP scopes selection) currently has unsaved changes.
     */
    const isDetailDirty = useCallback(() => dirtyCheck(methods.formState.isDirty), [dirtyCheck, methods.formState]);

    /**
     * Validates and saves the collector without navigating away, then re-queries the detail to
     * pick up the latest server state. Resolves true on success, false if validation fails.
     */
    const saveAndReloadDetail = useCallback(async () => {
        let validationPassed = true;
        await new Promise((resolve) => {
            handleSubmit(
                async (submitData, e) => {
                    await handleSubmitWithoutNavigate(submitData, e);
                    resolve();
                },
                () => {
                    validationPassed = false;
                    resolve();
                }
            )();
        });
        if (!validationPassed) {
            return false;
        }
        resetForm();
        // The ipScopes selection dirty flag is only ever set to true elsewhere; clear it here so a
        // follow-up transfer attempt does not re-trigger the "save & continue" prompt.
        setIpScopesSelectionIsDirty(false);
        return true;
    }, [handleSubmit, handleSubmitWithoutNavigate, resetForm]);

    const handleConfirmedDelete = useCallback(() => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(apiUrl, recordId),
            onDeleteGoTo: goToUrl,
        })();
    }, [recordId, formDeleteService, deleteWrapper, goToUrl]);

    useEffect(() => {
        const deletableUrl = '/ngp/v2/inventory/collectors/:_id/deletable';
        const collectorId = formData._id;

        if (collectorId) {
            const url = buildLinkUrl({ target: deletableUrl }, { _id: collectorId }, null);
            dataService
                .get(url)
                .then((response) => {
                    setDeletable(response.canDelete);
                })
                .catch((e) => {
                    if (e?.responseBody?.error) {
                        alertsActions.createErrorAlert(e.responseBody.error);
                    }
                });
        }
    }, [dataService, formData, alertsActions]);

    useEffect(() => {
        const transferrableUrl = '/ngp/v2/inventory/collectors/:_id/transferrable';
        const collectorId = formData._id;

        if (collectorId) {
            const url = buildLinkUrl({ target: transferrableUrl }, { _id: collectorId }, null);
            dataService
                .get(url)
                .then((response) => {
                    setTransferrableCollectors(response.data);
                })
                .catch((e) => {
                    if (e?.responseBody?.error) {
                        alertsActions.createErrorAlert(e.responseBody.error);
                    }
                });
        }
    }, [alertsActions, formData, dataService]);

    const handleSaveConfirmation = () => {
        if (mightDeleteDevices()) {
            setShowDeviceDeletionConfirmationDialog(true);
        } else {
            handleSubmit(handleSubmitFunction)();
        }
    };

    /**
     * This function compares the original collector scopes with the current
     * ones and will warn the user if they have modified the system in such a
     * way which could result in deleted devices.
     *
     * @returns {Boolean}
     */
    const mightDeleteDevices = () => {
        const originalCollector = formData;
        const currentCollector = getValues();
        // Subnets moved to other collectors are already gone on the server side, so exclude
        // them from the original list to avoid a false-positive device-deletion warning.
        const remainingOriginalScopes = _.reject(originalCollector.ipScopes, (scope) =>
            movedSubnetsRef.current.has(_.get(scope, 'value.subnet'))
        );
        const originalScopes = mapIps(remainingOriginalScopes);
        const updatedScopes = mapIps(currentCollector.ipScopes);

        // Remove any scopes which are identical between the original and updated sets. This will allow the
        // 'isScopeEncompassed' tests below to run much faster for any reasonable incremental changes, even
        // if there are thousands of total scopes.
        removeDuplicates(originalScopes, updatedScopes);

        // Check to make sure that every original included scope is still encompassed by one or more include
        // scopes in the updated set. Excluded scopes are not checked because they are not used when deciding
        // to delete devices in the back end.
        return !_.every(originalScopes, (scope) => isScopeEncompassed(scope, updatedScopes));

        function isScopeEncompassed(scope, scopeList) {
            return _.some(scopeList, (encompassingScope) => IPAddressUtilities.encompasses(scope, encompassingScope));
        }

        function mapIps(scopes) {
            const subnets = _(scopes)
                .filter((scope) => !scope.exclude)
                .map((scope) => _.get(scope, 'value.subnet'))
                .value();
            // Convert the subnet array into an object keyed by each subnet string, allowing
            // very fast access to each.
            return _.keyBy(subnets, (s) => s);
        }

        function removeDuplicates(origScopes) {
            _.forOwn(origScopes, (value, key) => {
                if (updatedScopes[key]) {
                    delete origScopes[key];
                    delete updatedScopes[key];
                }
            });
        }
    };

    return (
        <AdminPage
            pageId={buildPageId(COLLECTORS_FUNCTION_NAME)}
            breadcrumbs={breadcrumbs}
            actions={
                <Actions>
                    <ConfirmDeleteButton
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={false}
                        disabled={!deletable}
                    />
                    <Button onClick={handleSaveConfirmation} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                    <NgpTwoButtonDialog
                        title={saveTitle}
                        confirmButtonText={saveButtonText}
                        cancelButtonText={cancelButtonText}
                        open={showDeviceDeletionConfirmationDialog}
                        setOpen={setShowDeviceDeletionConfirmationDialog}
                        onConfirm={handleSubmit(handleSubmitFunction)}
                        isWarning
                        closeInTitleBar={false}
                    >
                        <div>{saveText}</div>
                    </NgpTwoButtonDialog>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized || saving || deleting}
                    loading={finding}
                    isDirtyFunc={dirtyCheck}
                    focusFirstField={false}
                >
                    <FormSection title={t`Collector Details`}>
                        <NgpTextField
                            fieldName={COLLECTORS_ADMIN_STATUS_FIELD}
                            label={t`Status`}
                            inputProps={{ size: 40, maxLength: 128 }}
                            disabled
                        />
                        <NgpTextField
                            fieldName={COLLECTORS_ADMIN_NAME_FIELD}
                            label={t`Collector Name`}
                            required
                            inputProps={{ size: 40, maxLength: 128 }}
                        />
                        <NgpTextarea
                            fieldName={COLLECTORS_ADMIN_DESCRIPTION_FIELD}
                            label={t`Collector Description`}
                            cols={60}
                            inputProps={{ maxLength: 225 }}
                        />
                    </FormSection>
                    <CollectorDetailIpScope
                        collector={formData}
                        transferrableCollectors={transferrableCollectors}
                        currentIpScopes={currentIpScopes}
                        setIpScopesSelectionIsDirty={setIpScopesSelectionIsDirty}
                        movedSubnetsRef={movedSubnetsRef}
                        editScopeSubnetFromUrl={editScopeSubnetFromUrl}
                        isDetailDirty={isDetailDirty}
                        saveAndReloadDetail={saveAndReloadDetail}
                        reloadDetail={resetForm}
                    />
                    <CollectorDetailvCenters collectorId={recordId} />
                    <CollectorDetailReadOnly collector={formData} />
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
